import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Award,
  Clock,
  LogIn as LogInIcon,
  LogOut,
  RefreshCw,
  UserCheck,
  UserX,
  Users2,
  ChevronRight,
  Mail,
  Phone,
  KeyRound,
  AlertTriangle,
  Download,
  Send,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { adminApi, tokenStorage } from "@/lib/api";

const BUCKET_META = [
  { key: "day1", label: "Day 1 · Documentation", tone: "border-amber-400/40 bg-amber-400/10 text-amber-100" },
  { key: "day3", label: "Day 3 · Training", tone: "border-blue-400/40 bg-blue-400/10 text-blue-100" },
  { key: "day7", label: "Day 7 · First Steps", tone: "border-violet-400/40 bg-violet-400/10 text-violet-100" },
  { key: "day11", label: "Day 11 · Check-in", tone: "border-emerald-400/40 bg-emerald-400/10 text-emerald-100" },
  { key: "graduated", label: "Graduated", tone: "border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#F0D788]" },
];

const ROLE_LABEL = {
  dsa: "DSA", advisor: "Advisor", agency: "Agency",
  associate: "Associate", sub_branch: "Sub-Branch", branch: "Branch",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminOnboardingBoard() {
  const navigate = useNavigate();
  const hasToken = Boolean(tokenStorage.get());
  const [board, setBoard] = useState(null);
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("day1"); // day1 | day3 | day7 | day11 | graduated | no_password_set
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkFlash, setBulkFlash] = useState(null); // { type, msg }

  const bulkResend = async () => {
    if (!window.confirm("Send a fresh 24-hour activation link to every partner who hasn't set their password yet?")) {
      return;
    }
    setBulkBusy(true);
    setBulkFlash(null);
    try {
      const { data } = await adminApi.post("/admin/onboarding/resend-activation-all", {});
      setBulkFlash({ type: "ok", msg: data.message || `${data.dispatched} email(s) queued.` });
      // refresh so the no-password bucket updates soon
      setTimeout(() => load(), 800);
    } catch (e) {
      setBulkFlash({ type: "err", msg: e?.response?.data?.detail || "Could not queue the emails." });
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadCsv = async (path, defaultName) => {
    try {
      const resp = await adminApi.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = defaultName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.detail || "CSV download failed.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, l] = await Promise.all([
        adminApi.get("/admin/onboarding/board"),
        adminApi.get("/admin/partner-logins/recent", { params: { limit: 30 } }),
      ]);
      setBoard(b.data);
      setLogins(l.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (hasToken) load();
  }, [hasToken, load]);

  if (!hasToken) return <Navigate to="/admin" replace />;

  const logout = async () => {
    try { await adminApi.post("/auth/logout"); }
    catch { /* best-effort server logout; local session is cleared regardless */ }
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  const counts = board?.counts || {};
  const noPwCount = counts.no_password_set || 0;
  const currentBucket = board?.buckets?.[tab] || [];

  return (
    <div data-testid="admin-onboarding-board" className="min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/90 backdrop-blur-md border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[110px] flex items-center justify-between gap-4">
          <button onClick={() => navigate("/")} className="text-left">
            <div className="font-display text-sm md:text-lg uppercase tracking-tight font-semibold whitespace-nowrap">
              SAHYOG FINANCE
              <br />
              <span className="text-[#D4AF37]">&amp; NOBLE INFINITY</span>
            </div>
            <div className="font-display text-[#D4AF37] text-[10px] md:text-xs tracking-[0.18em] uppercase mt-1">
              Admin · Onboarding Board
            </div>
          </button>
          <div className="flex items-center gap-3">
            <button
              data-testid="board-refresh"
              onClick={load}
              disabled={loading}
              className="text-white/80 hover:text-[#F0D788] inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/10 text-base font-semibold"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 text-sm border border-white/10"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 space-y-8">
        {/* Nav breadcrumbs */}
        <div className="flex items-center gap-3 text-xs">
          <Link to="/admin/applications" className="px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-white/70">
            ← Applications
          </Link>
          <Link to="/admin/onboarding-log" className="px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-white/70">
            Email Drip Log
          </Link>
          <Link to="/admin/partners" className="px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-white/70">
            Partners
          </Link>
        </div>

        {/* Summary strip */}
        <section className="rounded-2xl border border-[#D4AF37]/25 bg-[#0A1F44] px-6 py-5">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">Partner Onboarding</div>
          <h2 className="mt-1 font-display text-2xl text-white">
            {board ? `${board.total_active_partners} active partners in the funnel` : "Loading onboarding pipeline…"}
          </h2>
          <p className="mt-1 text-xs text-white/55">
            Auto-drip emails go out on Day 1 · 3 · 7 · 11. This board is the live view.
          </p>
        </section>

        {/* No-password warning card (always visible if > 0) */}
        {noPwCount > 0 && (
          <section
            data-testid="board-no-pw-alert"
            className="rounded-2xl border border-rose-400/40 bg-rose-500/10 p-5"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-rose-500/25 grid place-items-center shrink-0">
                <KeyRound size={18} className="text-rose-100" />
              </div>
              <div className="flex-1">
                <div className="text-rose-100 font-display text-lg">
                  {noPwCount} partner{noPwCount === 1 ? "" : "s"} haven't set their password yet
                </div>
                <div className="text-rose-100/75 text-xs mt-1">
                  Their 24-hour activation link may have expired. Send a fresh link in one click, or open the list to resend individually.
                </div>
                {bulkFlash && (
                  <div
                    data-testid="bulk-resend-flash"
                    className={`mt-3 text-xs rounded-lg border p-2 inline-flex items-start gap-1.5 ${
                      bulkFlash.type === "ok"
                        ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-100"
                        : "bg-rose-500/15 border-rose-400/40 text-rose-100"
                    }`}
                  >
                    {bulkFlash.type === "ok" ? <CheckCircle2 size={12} className="mt-0.5" /> : <AlertTriangle size={12} className="mt-0.5" />}
                    <span>{bulkFlash.msg}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button
                  data-testid="board-bulk-resend"
                  onClick={bulkResend}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] hover:brightness-110 text-xs uppercase tracking-wider font-semibold disabled:opacity-60"
                >
                  {bulkBusy ? <><Loader2 size={12} className="animate-spin" /> Sending…</> : <><Send size={12} /> Resend to all</>}
                </button>
                <button
                  onClick={() => setTab("no_password_set")}
                  className="px-4 py-2 rounded-full bg-rose-500/30 hover:bg-rose-500/40 border border-rose-400/40 text-rose-50 text-xs uppercase tracking-wider"
                >
                  View list <ChevronRight size={12} className="inline ml-1" />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Bucket tabs */}
        <section>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Onboarding funnel</div>
            <button
              data-testid="board-csv"
              onClick={() => downloadCsv("/admin/onboarding/board.csv", `sahyog-onboarding-board.csv`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 hover:border-[#D4AF37]/40 text-xs text-white/75"
            >
              <Download size={12} /> Export CSV
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {BUCKET_META.map((b) => (
              <button
                key={b.key}
                data-testid={`board-tab-${b.key}`}
                onClick={() => setTab(b.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-wider border transition ${
                  tab === b.key
                    ? `${b.tone} font-semibold`
                    : "bg-white/[0.04] border-white/10 text-white/70 hover:border-[#D4AF37]/40"
                }`}
              >
                {b.label}
                <span className="text-[10px] opacity-75">({counts[b.key] ?? 0})</span>
              </button>
            ))}
            <button
              data-testid="board-tab-no_password_set"
              onClick={() => setTab("no_password_set")}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-wider border transition ${
                tab === "no_password_set"
                  ? "bg-rose-500/20 border-rose-400/50 text-rose-100 font-semibold"
                  : "bg-white/[0.04] border-white/10 text-white/70 hover:border-rose-400/40"
              }`}
            >
              <AlertTriangle size={12} /> No password set
              <span className="text-[10px] opacity-75">({noPwCount})</span>
            </button>
          </div>

          <div
            data-testid={`board-bucket-${tab}`}
            className="mt-5 rounded-2xl border border-white/10 bg-[#0A1F44] overflow-hidden"
          >
            {loading && (
              <div className="p-10 text-center text-white/60 text-sm">Loading…</div>
            )}
            {!loading && currentBucket.length === 0 && (
              <div className="p-10 text-center text-white/55 text-sm">
                No partners currently in this bucket. 🎉
              </div>
            )}
            {!loading && currentBucket.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-white/[0.04] text-white/55 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="text-left p-4">Partner</th>
                    <th className="text-left p-4">Role</th>
                    <th className="text-left p-4">Contact</th>
                    <th className="text-left p-4">Registered</th>
                    <th className="text-left p-4">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBucket.map((p) => (
                    <tr key={p.id} data-testid={`board-row-${p.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="p-4 align-top">
                        <div className="font-display text-base">{p.name}</div>
                        <div className="text-[11px] text-white/45 mt-0.5">
                          Referral · <span className="font-mono">{p.referral_code || "—"}</span>
                        </div>
                      </td>
                      <td className="p-4 align-top">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#F0D788] text-[10px] uppercase tracking-wider">
                          {ROLE_LABEL[p.role] || p.role}
                        </span>
                      </td>
                      <td className="p-4 align-top text-xs text-white/70">
                        {p.email && (
                          <div className="flex items-center gap-1.5"><Mail size={11} className="text-[#D4AF37]" /> {p.email}</div>
                        )}
                        {p.phone && (
                          <div className="flex items-center gap-1.5 mt-0.5"><Phone size={11} className="text-[#D4AF37]" /> {p.phone}</div>
                        )}
                      </td>
                      <td className="p-4 align-top text-xs text-white/65 whitespace-nowrap">
                        {fmtTime(p.created_at)}
                        <div className="text-[10px] text-white/40 mt-0.5">
                          {p.age_days} day{p.age_days === 1 ? "" : "s"} ago
                        </div>
                      </td>
                      <td className="p-4 align-top">
                        <div className="flex flex-wrap gap-1">
                          {(p.completed_step_keys || []).map((k) => (
                            <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-400/30 text-emerald-200">
                              ✓ {k.split("_")[0].toUpperCase()}
                            </span>
                          ))}
                          {p.must_set_password && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 border border-rose-400/30 text-rose-200 inline-flex items-center gap-0.5">
                              <KeyRound size={9} /> No password
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Recent logins */}
        <section>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <LogInIcon size={16} className="text-[#D4AF37]" />
              <h2 className="font-display text-xl">Recent partner logins</h2>
              <span className="text-[10px] uppercase tracking-wider text-white/45">last 30 · admin gets an email · 1/hr per partner</span>
            </div>
            <button
              data-testid="logins-csv"
              onClick={() => downloadCsv("/admin/partner-logins/recent.csv", `sahyog-partner-logins.csv`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] border border-white/15 hover:border-[#D4AF37]/40 text-xs text-white/75"
            >
              <Download size={12} /> Export CSV
            </button>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0A1F44] overflow-hidden" data-testid="recent-logins-table">
            {logins.length === 0 ? (
              <div className="p-10 text-center text-white/55 text-sm">No partner logins captured yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-white/[0.04] text-white/55 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="text-left p-4">Partner</th>
                    <th className="text-left p-4">Role</th>
                    <th className="text-left p-4">Contact</th>
                    <th className="text-left p-4">IP</th>
                    <th className="text-left p-4">When</th>
                  </tr>
                </thead>
                <tbody>
                  {logins.map((row) => (
                    <tr key={row.id} data-testid={`login-row-${row.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                      <td className="p-4 align-top">
                        <div className="font-display text-base">{row.partner_name || "—"}</div>
                        <div className="text-[11px] text-white/45 mt-0.5">
                          Referral · <span className="font-mono">{row.referral_code || "—"}</span>
                        </div>
                      </td>
                      <td className="p-4 align-top">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#F0D788] text-[10px] uppercase tracking-wider">
                          {ROLE_LABEL[row.partner_role] || row.partner_role}
                        </span>
                      </td>
                      <td className="p-4 align-top text-xs text-white/70">
                        {row.partner_email && (
                          <div className="flex items-center gap-1.5"><Mail size={11} className="text-[#D4AF37]" /> {row.partner_email}</div>
                        )}
                        {row.partner_phone && (
                          <div className="flex items-center gap-1.5 mt-0.5"><Phone size={11} className="text-[#D4AF37]" /> {row.partner_phone}</div>
                        )}
                      </td>
                      <td className="p-4 align-top font-mono text-xs text-white/60">{row.ip || "—"}</td>
                      <td className="p-4 align-top text-xs text-white/65 whitespace-nowrap">
                        {fmtTime(row.at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
